import { api, unwrapList } from '../../../shared/lib/axios';

/**
 * API client da Base Comercial (US-COM-005, change
 * `gestao-base-comercial`).
 *
 * **Cada registro é uma VERSÃO**, não uma Base: `id` identifica a versão
 * exata e `versionGroupId` agrupa as versões da mesma Base. Ao persistir
 * a Base aplicada em algum processo, guarde o `id` — é o que garante que
 * substituir a Base não altere condições já acordadas (RN-COM-021/026).
 */

/** Situações da Base Comercial (Seção 7 da spec). */
export type SituacaoBaseComercial =
  | 'em_elaboracao'
  | 'em_revisao'
  | 'em_aprovacao'
  | 'aprovada'
  | 'publicada'
  | 'vigente'
  | 'suspensa'
  | 'expirada'
  | 'cancelada'
  | 'substituida';

/** Moedas suportadas (RN-COM-011). */
export type MoedaBaseComercial = 'BRL' | 'USD' | 'EUR';

/** Periodicidade de reajuste (RN-COM-013). */
export type ReajustePeriodicidade = 'nenhuma' | 'mensal' | 'semestral' | 'anual' | 'indice';

/** Categorias de anexo (RN-COM-028). */
export type CategoriaAnexoBaseComercial =
  | 'tabela_precos'
  | 'acordo_comercial'
  | 'politica_desconto'
  | 'documento_negociacao'
  | 'outro';

/** Os quatro domínios administráveis (design §D7). */
export type DominioBaseComercial = 'servicos' | 'perfis' | 'precificacao' | 'indices';

export interface DominioItem {
  id: string;
  slug: string;
  nome: string;
  ordem: number;
  ativo: boolean;
  requerTaxaPublicada?: boolean;
}

/**
 * Campos `numeric` chegam como string do Postgres para não perder
 * precisão — formatar com os helpers de `utils/format`.
 */
export interface BaseComercialListItem {
  id: string;
  codigo: string;
  nome: string;
  clientId: string;
  responsavelComercialId: string;
  versionGroupId: string;
  versao: number;
  situacao: SituacaoBaseComercial;
  modalidade: string;
  escopo: string | null;
  dataInicio: string;
  dataFim: string | null;
  moeda: MoedaBaseComercial;
  valorTotal: string;
  receitaEstimada: string;
  margemEstimadaValor: string | null;
  margemEstimadaPercentual: string | null;
  reajustePendente: boolean;
  permiteCoexistencia: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BaseComercial extends BaseComercialListItem {
  supersededById: string | null;
  descontoMaximoPercentual: string | null;
  reajustePeriodicidade: ReajustePeriodicidade;
  reajusteIndexSlug: string | null;
  reajustePercentualFixo: string | null;
  proximaDataReajuste: string | null;
  requerAprovacaoEletronica: boolean;
  requerRevisaoCondicoes: boolean;
  canceladoPor: string | null;
  canceladoEm: string | null;
  motivoCancelamento: string | null;
  condicoesFaturamento: string | null;
  observacoesTributarias: string | null;
  observacoes: string | null;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface BaseComercialServico {
  id: string;
  commercialBaseId: string;
  serviceTypeSlug: string;
  descricao: string | null;
  ordem: number;
}

export interface BaseComercialItem {
  id: string;
  commercialBaseId: string;
  commercialBaseServiceId: string;
  professionalProfileSlug: string;
  pricingModelSlug: string;
  quantidade: string;
  valorUnitario: string;
  custoUnitario: string | null;
  valorTotalItem: string | null;
  custoTotalItem: string | null;
  vigenciaInicio: string | null;
  vigenciaFim: string | null;
  observacoes: string | null;
  ordem: number;
}

export interface BaseComercialHistorico {
  id: string;
  commercialBaseId: string;
  versionGroupId: string;
  versao: number;
  action: string;
  campo: string | null;
  valorAnterior: string | null;
  valorNovo: string | null;
  observacao: string | null;
  changedBy: string | null;
  changedAt: string;
}

export interface BaseComercialAprovacao {
  id: string;
  commercialBaseId: string;
  solicitadoPor: string | null;
  solicitadoEm: string;
  approverId: string | null;
  decisao: 'pendente' | 'aprovada' | 'rejeitada';
  decididoEm: string | null;
  justificativa: string | null;
  conteudoHash: string | null;
}

export interface BaseComercialAnexo {
  id: string;
  versionGroupId: string;
  versaoOrigem: number;
  categoria: CategoriaAnexoBaseComercial;
  nomeArquivo: string;
  mimeType: string | null;
  tamanhoBytes: string | null;
  descricao: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface VersoesResponse {
  versionGroupId: string;
  codigo: string;
  data: Array<BaseComercial & { vigente: boolean }>;
}

export interface ListBasesComerciaisParams {
  clientId?: string;
  responsavelComercialId?: string;
  situacao?: SituacaoBaseComercial;
  modalidade?: string;
  moeda?: MoedaBaseComercial;
  reajustePendente?: boolean;
  todasAsVersoes?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListBasesComerciaisResponse {
  data: BaseComercialListItem[];
  total: number;
  limit: number;
  offset: number;
}

export const BASE_COMERCIAL_KEYS = {
  all: ['base-comercial'] as const,
  list: (params?: ListBasesComerciaisParams) => ['base-comercial', 'list', params] as const,
  detail: (id: string) => ['base-comercial', 'detail', id] as const,
  services: (id: string) => ['base-comercial', 'services', id] as const,
  items: (id: string) => ['base-comercial', 'items', id] as const,
  versions: (id: string) => ['base-comercial', 'versions', id] as const,
  history: (id: string) => ['base-comercial', 'history', id] as const,
  approvals: (id: string) => ['base-comercial', 'approvals', id] as const,
  attachments: (id: string) => ['base-comercial', 'attachments', id] as const,
  pendingApproval: () => ['base-comercial', 'pending-approval'] as const,
  dominio: (tipo: DominioBaseComercial) => ['base-comercial', 'dominio', tipo] as const,
};

/** Ignora `undefined`/`null`/'' — reset de filtro se faz por omissão. */
function toQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// -------- Leitura ----------------------------------------------------

export async function listarBasesComerciais(
  params?: ListBasesComerciaisParams,
): Promise<ListBasesComerciaisResponse> {
  const { data } = await api.get<ListBasesComerciaisResponse>(
    `/commercial-bases${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarBaseComercial(id: string): Promise<BaseComercial> {
  const { data } = await api.get<BaseComercial>(`/commercial-bases/${id}`);
  return data;
}

export async function listarServicos(id: string): Promise<BaseComercialServico[]> {
  const { data } = await api.get<BaseComercialServico[] | { data: BaseComercialServico[] }>(
    `/commercial-bases/${id}/services`,
  );
  return unwrapList<BaseComercialServico>(data);
}

export async function listarItens(id: string): Promise<BaseComercialItem[]> {
  const { data } = await api.get<BaseComercialItem[] | { data: BaseComercialItem[] }>(
    `/commercial-bases/${id}/items`,
  );
  return unwrapList<BaseComercialItem>(data);
}

export async function listarVersoes(id: string): Promise<VersoesResponse> {
  const { data } = await api.get<VersoesResponse>(`/commercial-bases/${id}/versions`);
  return data;
}

export async function listarHistorico(
  id: string,
  params?: { limit?: number; offset?: number },
): Promise<{ data: BaseComercialHistorico[]; total: number }> {
  const { data } = await api.get<{ data: BaseComercialHistorico[]; total: number }>(
    `/commercial-bases/${id}/history${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function listarAprovacoes(id: string): Promise<BaseComercialAprovacao[]> {
  const { data } = await api.get<BaseComercialAprovacao[] | { data: BaseComercialAprovacao[] }>(
    `/commercial-bases/${id}/approvals`,
  );
  return unwrapList<BaseComercialAprovacao>(data);
}

export async function listarPendentesAprovacao(params?: {
  limit?: number;
  offset?: number;
}): Promise<{
  data: BaseComercialListItem[];
  total: number;
  limit?: number;
  offset?: number;
}> {
  const { data } = await api.get<{
    data: BaseComercialListItem[];
    total: number;
    limit?: number;
    offset?: number;
  }>(`/commercial-bases/pending-approval${toQueryString(params as Record<string, unknown>)}`);
  return data;
}

export async function listarAnexos(id: string): Promise<BaseComercialAnexo[]> {
  const { data } = await api.get<BaseComercialAnexo[] | { data: BaseComercialAnexo[] }>(
    `/commercial-bases/${id}/attachments`,
  );
  return unwrapList<BaseComercialAnexo>(data);
}

export async function listarDominio(
  tipo: DominioBaseComercial,
  includeInactive = false,
): Promise<DominioItem[]> {
  const { data } = await api.get<DominioItem[] | { data: DominioItem[] }>(
    `/commercial-base-domains/${tipo}${includeInactive ? '?includeInactive=true' : ''}`,
  );
  return unwrapList<DominioItem>(data);
}

// -------- Cadastro ---------------------------------------------------

export interface CreateBaseComercialPayload {
  nome: string;
  clientId: string;
  responsavelComercialId: string;
  dataInicio: string;
  modalidade: string;
  dataFim?: string | null;
  escopo?: string;
  moeda?: MoedaBaseComercial;
  descontoMaximoPercentual?: number | null;
  reajustePeriodicidade?: ReajustePeriodicidade;
  reajusteIndexSlug?: string | null;
  reajustePercentualFixo?: number | null;
  permiteCoexistencia?: boolean;
  requerAprovacaoEletronica?: boolean;
  condicoesFaturamento?: string;
  observacoesTributarias?: string;
  observacoes?: string;
}

/** `clientId` fica de fora: trocar o cliente é criar outra Base. */
export type UpdateBaseComercialPayload = Partial<Omit<CreateBaseComercialPayload, 'clientId'>>;

export async function criarBaseComercial(
  payload: CreateBaseComercialPayload,
): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>('/commercial-bases', payload);
  return data;
}

/**
 * **Atenção:** sobre versão publicada ou vigente, o backend gera uma
 * nova versão e retorna **ela** (RN-COM-005) — o `id` da resposta pode
 * ser diferente do enviado. Quem chama precisa redirecionar para o id
 * retornado, senão o usuário continua vendo a versão antiga.
 */
export async function atualizarBaseComercial(
  id: string,
  payload: UpdateBaseComercialPayload,
): Promise<BaseComercial> {
  const { data } = await api.patch<BaseComercial>(`/commercial-bases/${id}`, payload);
  return data;
}

export async function removerBaseComercial(id: string): Promise<{ id: string }> {
  const { data } = await api.delete<{ id: string }>(`/commercial-bases/${id}`);
  return data;
}

// -------- Estrutura (serviços e itens) -------------------------------

export interface AddServicoPayload {
  serviceTypeSlug: string;
  descricao?: string;
  ordem?: number;
}

export interface AddItemPayload {
  commercialBaseServiceId: string;
  professionalProfileSlug: string;
  pricingModelSlug: string;
  quantidade?: number;
  valorUnitario: number;
  custoUnitario?: number | null;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  observacoes?: string;
  ordem?: number;
}

export type UpdateItemPayload = Partial<Omit<AddItemPayload, 'commercialBaseServiceId'>>;

export async function adicionarServico(
  id: string,
  payload: AddServicoPayload,
): Promise<BaseComercialServico> {
  const { data } = await api.post<BaseComercialServico>(
    `/commercial-bases/${id}/services`,
    payload,
  );
  return data;
}

export async function removerServico(id: string, servicoId: string): Promise<{ id: string }> {
  const { data } = await api.delete<{ id: string }>(
    `/commercial-bases/${id}/services/${servicoId}`,
  );
  return data;
}

export async function adicionarItem(
  id: string,
  payload: AddItemPayload,
): Promise<BaseComercialItem> {
  const { data } = await api.post<BaseComercialItem>(`/commercial-bases/${id}/items`, payload);
  return data;
}

export async function atualizarItem(
  id: string,
  itemId: string,
  payload: UpdateItemPayload,
): Promise<BaseComercialItem> {
  const { data } = await api.patch<BaseComercialItem>(
    `/commercial-bases/${id}/items/${itemId}`,
    payload,
  );
  return data;
}

export async function removerItem(id: string, itemId: string): Promise<{ id: string }> {
  const { data } = await api.delete<{ id: string }>(`/commercial-bases/${id}/items/${itemId}`);
  return data;
}

// -------- Ciclo de vida ----------------------------------------------
// Um endpoint por evento de negócio; a situação nunca é enviada
// diretamente (design §D6).

export async function solicitarAprovacao(id: string): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/request-approval`);
  return data;
}

export async function confirmarRevisaoCondicoes(id: string): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(
    `/commercial-bases/${id}/confirm-conditions-review`,
  );
  return data;
}

export async function aprovarBaseComercial(
  id: string,
  justificativa?: string,
): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/approve`, {
    justificativa,
  });
  return data;
}

export async function rejeitarBaseComercial(
  id: string,
  justificativa: string,
): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/reject`, {
    justificativa,
  });
  return data;
}

export async function publicarBaseComercial(id: string): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/publish`);
  return data;
}

export async function suspenderBaseComercial(
  id: string,
  motivo: string,
): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/suspend`, { motivo });
  return data;
}

export async function reativarBaseComercial(id: string): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/reactivate`);
  return data;
}

export async function substituirBaseComercial(
  id: string,
  novaVersaoId: string,
  motivo?: string,
): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/supersede`, {
    novaVersaoId,
    ...(motivo ? { motivo } : {}),
  });
  return data;
}

export async function cancelarBaseComercial(id: string, motivo: string): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/cancel`, { motivo });
  return data;
}

export async function gerarNovaVersao(id: string): Promise<BaseComercial> {
  const { data } = await api.post<BaseComercial>(`/commercial-bases/${id}/new-version`);
  return data;
}

// -------- Anexos -----------------------------------------------------

export async function enviarAnexo(
  id: string,
  file: File,
  meta: { categoria?: CategoriaAnexoBaseComercial; descricao?: string },
): Promise<BaseComercialAnexo> {
  const form = new FormData();
  form.append('file', file);
  if (meta.categoria) form.append('categoria', meta.categoria);
  if (meta.descricao) form.append('descricao', meta.descricao);

  const { data } = await api.post<BaseComercialAnexo>(
    `/commercial-bases/${id}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function removerAnexo(id: string, anexoId: string): Promise<void> {
  await api.delete(`/commercial-bases/${id}/attachments/${anexoId}`);
}

export function urlDownloadAnexo(id: string, anexoId: string): string {
  return `/commercial-bases/${id}/attachments/${anexoId}/download`;
}
